import { orientationTransform, readExif, type ExifSummary } from './exif.js';

/**
 * Browser-side image preparation. On this product it is not an optimisation — it is the feature.
 *
 * The person using this app is a shop owner on patchy 4G in a market street, photographing a
 * design reference, the fabric and the finished garment. Their phone produces 3–6MB files.
 * Uploading one of those takes tens of seconds on a good day, fails outright on a bad one, and
 * costs them data they are paying for by the megabyte. So nothing leaves the device until it has
 * been reduced to roughly 1600px on its long edge at about 300KB — a twentieth of the size, and
 * still more detail than any screen in the shop can show.
 *
 * Three other things happen here, all of them consequences of re-encoding through a canvas:
 *
 *  - EXIF orientation is applied to the PIXELS, so a portrait photo is not displayed on its side;
 *  - all metadata is destroyed, including the GPS block that would otherwise attach the shop's
 *    location to every photograph it ever sends a customer;
 *  - a small thumbnail is produced alongside, so a list of twenty orders costs twenty 15KB
 *    images rather than twenty 300KB ones.
 *
 * The codec is injected. The real one uses `createImageBitmap` and a canvas, which needs a real
 * browser; separating it out means the sizing arithmetic and the quality-search loop — where the
 * bugs actually live — are tested directly.
 */

/** Long edge of the stored image. Comfortably above any screen a shop will view it on. */
export const FULL_LONG_EDGE_PX = 1600;
/** The size budget the quality search aims for. */
export const FULL_TARGET_BYTES = 300 * 1024;
export const THUMB_LONG_EDGE_PX = 320;
export const THUMB_TARGET_BYTES = 40 * 1024;

/** JPEG: universally supported, and far smaller than PNG for a photograph. */
export const OUTPUT_CONTENT_TYPE = 'image/jpeg';

/** Tried in order until the result fits the budget. */
export const QUALITY_LADDER = [0.82, 0.72, 0.62, 0.5, 0.4] as const;
/** If even the lowest quality overshoots, the image is shrunk and the ladder tried again. */
export const LONG_EDGE_LADDER = [FULL_LONG_EDGE_PX, 1280, 1024] as const;

export interface ImageSize {
  width: number;
  height: number;
}

export interface DecodedImage extends ImageSize {
  /** Whatever the codec draws from — an ImageBitmap or an HTMLImageElement. */
  source: unknown;
  /**
   * Orientation the codec still has to apply when drawing. Undefined when the decoder already
   * did it (`createImageBitmap` with `imageOrientation: 'from-image'`).
   */
  pendingOrientation?: number | undefined;
  close?: () => void;
}

/**
 * The mark to burn into the pixels, when the shop has asked for one.
 *
 * BURNED IN, not overlaid at display time. An overlay is a decoration on our own screen; the
 * point of a watermark is that the image a shop sends a customer — and the image a customer
 * screenshots off that message — carries the shop's name. Doing it here, in the same canvas
 * pass that resizes and strips the EXIF, costs nothing extra and is the only place the bytes
 * pass through.
 */
export interface WatermarkSpec {
  text: string;
  position: 'bottom_right' | 'bottom_left' | 'top_right' | 'top_left' | 'centre';
  /** A whole percentage, as the settings screen's slider produces it. */
  opacity: number;
}

export interface ImageCodec {
  decode(blob: Blob, orientation: number | undefined): Promise<DecodedImage>;
  encode(
    image: DecodedImage,
    size: ImageSize,
    type: string,
    quality: number,
    watermark?: WatermarkSpec,
  ): Promise<Blob>;
}

export interface PreparedVariant extends ImageSize {
  blob: Blob;
  quality: number;
}

export interface PreparedImage {
  full: PreparedVariant;
  thumb: PreparedVariant;
  contentType: string;
  originalBytes: number;
  originalFilename: string;
  /** What was found in the original. Reported so the UI — and tests — can see it was there. */
  sourceExif: ExifSummary;
}

/**
 * Fit an image inside a square of `longEdge`, preserving the aspect ratio and NEVER upscaling —
 * enlarging a small image adds bytes and no detail.
 */
export function computeTargetSize(source: ImageSize, longEdge: number): ImageSize {
  const largest = Math.max(source.width, source.height);
  if (largest <= longEdge || largest === 0) {
    return { width: source.width, height: source.height };
  }
  const scale = longEdge / largest;
  return {
    width: Math.max(1, Math.round(source.width * scale)),
    height: Math.max(1, Math.round(source.height * scale)),
  };
}

/** EXIF only lives in the first few kilobytes; a 6MB photo is never read into memory whole. */
const EXIF_SCAN_BYTES = 128 * 1024;

/**
 * `Blob.arrayBuffer()` is not universal. Safari only grew it in 14, and the Android WebViews on
 * the cheap phones this product is aimed at can be older still, so the ancient-but-everywhere
 * FileReader is kept as a fallback rather than letting orientation handling silently switch off.
 */
export async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return await blob.arrayBuffer();
  return await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('The image could not be read'));
    reader.readAsArrayBuffer(blob);
  });
}

async function readSourceExif(file: Blob): Promise<ExifSummary> {
  try {
    return readExif(await blobToArrayBuffer(file.slice(0, EXIF_SCAN_BYTES)));
  } catch {
    return { present: false, orientation: undefined, hasGps: false };
  }
}

/**
 * Encode down the quality ladder — and then down the size ladder — until the result fits the
 * budget. Returns the first blob under budget, or the smallest one produced if none fit, because
 * a slightly-too-large image is a far better outcome than a failed upload.
 */
export async function encodeWithinBudget(
  codec: ImageCodec,
  image: DecodedImage,
  options: {
    longEdges: readonly number[];
    targetBytes: number;
    qualities: readonly number[];
    type?: string;
    watermark?: WatermarkSpec | undefined;
  },
): Promise<PreparedVariant> {
  const type = options.type ?? OUTPUT_CONTENT_TYPE;
  let smallest: PreparedVariant | undefined;

  for (const longEdge of options.longEdges) {
    const size = computeTargetSize(image, longEdge);
    for (const quality of options.qualities) {
      const blob = await codec.encode(image, size, type, quality, options.watermark);
      const candidate: PreparedVariant = { blob, quality, ...size };
      if (blob.size <= options.targetBytes) return candidate;
      if (!smallest || blob.size < smallest.blob.size) smallest = candidate;
    }
  }

  // Unreachable unless both ladders are empty, which would be a programming error.
  if (!smallest) throw new Error('encodeWithinBudget was given no sizes to try');
  return smallest;
}

export interface PrepareImageOptions {
  longEdge?: number;
  targetBytes?: number;
  thumbLongEdge?: number;
  thumbTargetBytes?: number;
  /**
   * The shop's watermark, when it has one switched on. Applied to the FULL image only — a
   * 320px thumbnail with text stamped across it is illegible noise, and nobody screenshots a
   * thumbnail.
   */
  watermark?: WatermarkSpec | undefined;
}

/**
 * Turn a camera original into the two objects that get uploaded. This is the only function the
 * uploader calls; everything above it is here to be tested on its own.
 */
export async function prepareImage(
  file: File,
  codec: ImageCodec,
  options: PrepareImageOptions = {},
): Promise<PreparedImage> {
  const sourceExif = await readSourceExif(file);
  const decoded = await codec.decode(file, sourceExif.orientation);

  try {
    const full = await encodeWithinBudget(codec, decoded, {
      longEdges: options.longEdge
        ? [options.longEdge, ...LONG_EDGE_LADDER.slice(1)]
        : LONG_EDGE_LADDER,
      targetBytes: options.targetBytes ?? FULL_TARGET_BYTES,
      qualities: QUALITY_LADDER,
      watermark: options.watermark,
    });

    const thumb = await encodeWithinBudget(codec, decoded, {
      longEdges: [options.thumbLongEdge ?? THUMB_LONG_EDGE_PX],
      targetBytes: options.thumbTargetBytes ?? THUMB_TARGET_BYTES,
      qualities: [0.72, 0.6, 0.45],
    });

    return {
      full,
      thumb,
      contentType: OUTPUT_CONTENT_TYPE,
      originalBytes: file.size,
      originalFilename: file.name,
      sourceExif,
    };
  } finally {
    decoded.close?.();
  }
}

// --- The real browser codec ------------------------------------------------------------------

/**
 * Decodes with `createImageBitmap`, which is both faster and lower-peak-memory than an `<img>`
 * on a phone, and which applies EXIF orientation itself when asked. Falls back to an `<img>` —
 * and to applying the orientation by hand at draw time — where that is unavailable.
 *
 * Needs a real canvas, so it cannot run under jsdom; the tests exercise the pipeline through a
 * stub codec instead, and this path is verified in a browser.
 */
export const browserImageCodec: ImageCodec = {
  async decode(blob: Blob, orientation: number | undefined): Promise<DecodedImage> {
    if (typeof createImageBitmap === 'function') {
      try {
        const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
        return {
          width: bitmap.width,
          height: bitmap.height,
          source: bitmap,
          close: () => bitmap.close(),
        };
      } catch {
        // Safari has historically rejected the options bag; fall through to the <img> path.
      }
    }

    const url = URL.createObjectURL(blob);
    try {
      const image = await loadImageElement(url);
      const { swapsAxes } = orientationTransform(orientation);
      return {
        // Report the size the image will APPEAR, so the sizing arithmetic is orientation-aware.
        width: swapsAxes ? image.naturalHeight : image.naturalWidth,
        height: swapsAxes ? image.naturalWidth : image.naturalHeight,
        source: image,
        pendingOrientation: orientation,
      };
    } finally {
      URL.revokeObjectURL(url);
    }
  },

  async encode(
    image: DecodedImage,
    size: ImageSize,
    type: string,
    quality: number,
    watermark?: WatermarkSpec,
  ): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser cannot prepare images for upload');

    // Better downscaling than the default nearest-neighbour-ish path, which turns fabric
    // texture into noise.
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';

    applyOrientation(context, image.pendingOrientation, size);
    const drawSize = orientationTransform(image.pendingOrientation).swapsAxes
      ? { width: size.height, height: size.width }
      : size;
    context.drawImage(image.source as CanvasImageSource, 0, 0, drawSize.width, drawSize.height);

    // After the orientation transform is undone, so the mark reads the right way up whatever
    // the phone was doing when the photograph was taken.
    if (watermark) {
      context.setTransform(1, 0, 0, 1, 0, 0);
      drawWatermark(context, size, watermark);
    }

    return await canvasToBlob(canvas, type, quality);
  },
};

/**
 * The canonical EXIF-orientation transform table. `width`/`height` are the FINAL canvas
 * dimensions; for the four orientations that turn the image a quarter-turn, the draw call uses
 * them swapped (handled by the caller).
 */
function applyOrientation(
  context: CanvasRenderingContext2D,
  orientation: number | undefined,
  size: ImageSize,
): void {
  const { width, height } = size;
  switch (orientation) {
    case 2:
      context.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      context.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      context.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      context.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      context.transform(0, 1, -1, 0, width, 0);
      break;
    case 7:
      context.transform(0, -1, -1, 0, width, height);
      break;
    case 8:
      context.transform(0, -1, 1, 0, 0, height);
      break;
    default:
      break;
  }
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The image could not be read'));
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('The image could not be prepared'));
      },
      type,
      quality,
    );
  });
}

/**
 * The mark itself: the shop's own words, in a size proportional to the image.
 *
 * Proportional and not fixed, because the same 12px caption is a shout on a thumbnail and a
 * whisper on a 1600px photograph. Drawn with a soft shadow underneath so it stays readable over
 * both a white blouse and a dark lehenga — a single colour is invisible against one or the
 * other, and a watermark nobody can read is one that has cost bytes for nothing.
 */
export function drawWatermark(
  context: CanvasRenderingContext2D,
  size: ImageSize,
  watermark: WatermarkSpec,
): void {
  const text = watermark.text.trim();
  if (text.length === 0) return;

  const fontSize = Math.max(12, Math.round(Math.min(size.width, size.height) * 0.045));
  const padding = Math.round(fontSize * 0.75);

  context.save();
  context.globalAlpha = Math.min(1, Math.max(0.1, watermark.opacity / 100));
  context.font = `600 ${String(fontSize)}px sans-serif`;
  context.textBaseline = 'alphabetic';

  const centred = watermark.position === 'centre';
  const top = watermark.position === 'top_left' || watermark.position === 'top_right';
  const left = watermark.position === 'top_left' || watermark.position === 'bottom_left';

  const metrics = context.measureText(text);
  const x = centred
    ? (size.width - metrics.width) / 2
    : left
      ? padding
      : size.width - metrics.width - padding;
  const y = centred ? size.height / 2 : top ? padding + fontSize : size.height - padding;

  context.fillStyle = 'rgba(0, 0, 0, 0.45)';
  context.fillText(text, x + 1, y + 1);
  context.fillStyle = '#ffffff';
  context.fillText(text, x, y);
  context.restore();
}
